import React from 'react';

import { Divider, Grid, Typography } from '@mui/material';
import { uniqueId } from 'es-toolkit/compat';

import { formatMoney, sum } from '@clearverify/utils';

import { type EmploymentDataSummaryIncomeInformationInfoFragment } from 'src/api';

type EmploymentDataSummaryIncomeBlockProps = {
  payrollItem: EmploymentDataSummaryIncomeInformationInfoFragment;
};

export const EmploymentDataSummaryIncomeBlock: React.FC<EmploymentDataSummaryIncomeBlockProps> = ({
  payrollItem,
}) => {
  const firstColonFields = [
    { label: 'Regular', value: formatMoney(payrollItem.baseWage) },
    { label: 'Overtime', value: formatMoney(payrollItem.overtimeWage) },
    { label: 'Commission', value: formatMoney(payrollItem.commissionWage) },
    { label: 'Bonus', value: formatMoney(payrollItem.bonusesWage) },
    { label: 'Holiday', value: formatMoney(payrollItem.holidayWage) },
  ];
  const secondColonFields = [
    { label: 'Pension', value: formatMoney(payrollItem.pension) },
    { label: 'Severance', value: formatMoney(payrollItem.severance) },
    { label: 'Sick', value: formatMoney(payrollItem.sickWage) },
    { label: 'Incentive', value: formatMoney(payrollItem.incentiveWage) },
    { label: 'PTO', value: formatMoney(payrollItem.ptoWage) },
  ];
  const thirdColonFields = [
    { label: 'Tips', value: formatMoney(payrollItem.tips) },
    { label: 'Vacation', value: formatMoney(payrollItem.vacationWage) },
    { label: 'PTO Payout', value: formatMoney(payrollItem.ptoPayoutWage) },
    { label: 'Sick Time Payout', value: formatMoney(payrollItem.sickTimePayoutWage) },
    { label: 'Vacation Time Payout', value: formatMoney(payrollItem.vacationTimePayoutWage) },
    {
      label: 'Miscellaneous',
      value: formatMoney(sum(payrollItem.miscWage, payrollItem.otherWage)),
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid>
        <Typography fontWeight="bold">{`Year: ${payrollItem.year} | YTD Total: ${formatMoney(
          payrollItem.totalWage,
        )}`}</Typography>
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      <Grid container size={12}>
        <Grid size={4}>
          <Grid container spacing={2}>
            {renderFields(firstColonFields)}
          </Grid>
        </Grid>

        <Grid size={4}>
          <Grid container spacing={2}>
            {renderFields(secondColonFields)}
          </Grid>
        </Grid>

        <Grid size={4}>
          <Grid container spacing={2}>
            {renderFields(thirdColonFields)}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

type InformationFieldType = {
  label: string;
  value: string;
};

const renderFields = (fieldsList: InformationFieldType[]) => {
  return fieldsList.map(field => (
    <React.Fragment key={uniqueId(field.value)}>
      <Grid
        size={{
          xs: 12,
          md: 7,
        }}
      >
        <Typography component="span" variant="body1" color="text.secondary">
          {field.label}
        </Typography>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 5,
        }}
      >
        {field.value}
      </Grid>
    </React.Fragment>
  ));
};
